import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { ArrowLeft, Download, Save, Square } from 'lucide-react';
import Button from '@components/atoms/ui-kit/button.tsx';
import {
  LiveEditorCache,
  LiveEditorRow
} from '@components/editors/realtime-editor/live/live-editor-row.tsx';
import { useLiveTranscript } from '@utils/providers/use-live-transcript.ts';
import { liveUtteranceText } from '@utils/editors/live-transcript-state.ts';
import { liveTranscriptStatusKey } from '@utils/editors/live-transcript-status.ts';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useLiveSections } from '@utils/providers/use-live-sections.ts';
import { LiveSectionsToolbar } from '@components/editors/realtime-editor/live/live-sections-toolbar.tsx';
import { useLiveFollowOutput } from '@utils/hooks/use-live-follow-output.ts';
import { LiveTranscriptSectionNotices } from '@components/editors/realtime-editor/live/live-transcript-section-notices.tsx';
import { LiveRecordings } from '@components/editors/realtime-editor/live/live-recordings.tsx';
import { LiveTranscriptProps } from './live-transcript-props.ts';
export default function LiveTranscriptEditor({
  sessionId: suppliedId,
  embedded = false,
  toolsSlot,
  onStateChange,
  freshResetBusy = false
}: LiveTranscriptProps = {}) {
  const { sessionId: routeId = '' } = useParams();
  const sessionId = suppliedId || routeId;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getColour } = useThemeContext();
  const live = useLiveTranscript(sessionId);
  const sections = useLiveSections(
    sessionId,
    live.scope,
    live.rows,
    live.canEdit,
    live.save,
    live.refreshRange
  );
  const visibleRows = sections.rows;
  const draftExit = useRef<((after: () => void) => void) | null>(null);
  const [sectionFormOpen, setSectionFormOpen] = useState(false);
  const onDraftExitChange = useCallback(
    (requestExit: ((after: () => void) => void) | null) => {
      draftExit.current = requestExit;
      setSectionFormOpen(open =>
        open === !!requestExit ? open : !!requestExit
      );
    },
    []
  );
  const lastSequence = useRef(0);
  lastSequence.current = live.rows.at(-1)?.segment.sequence ?? 0;
  const refreshRecording = useCallback(
    () =>
      lastSequence.current
        ? live.refreshRange(1, lastSequence.current)
        : Promise.resolve(true),
    [live.refreshRange]
  );
  const canEdit =
    !freshResetBusy &&
    live.canEdit &&
    !sections.selected?.completed &&
    !sections.completing;
  const saving = live.saving || sections.busy;
  const saveLabel = saving
    ? t('saving')
    : t(sections.selected ? 'live-save-section' : 'live-save-corrections');
  const liveCache = useMemo<LiveEditorCache>(() => new Map(), [live.scope]);
  const frozenCache = useMemo<LiveEditorCache>(
    () => new Map(),
    [sections.renderScope]
  );
  const cache = sections.selected?.completed ? frozenCache : liveCache;
  const list = useRef<VirtuosoHandle>(null);
  const follow = useLiveFollowOutput(!!sections.selected);
  const ended = ['stopped', 'failed'].includes(live.owner?.state || '');
  const statusLabel = t(
    liveTranscriptStatusKey(live.loading, live.owner, live.connected)
  );
  useEffect(() => {
    onStateChange?.({
      sessionId,
      scope: live.scope,
      ended,
      dirty: live.dirty,
      label: statusLabel,
      owner: live.owner,
      saving,
      discard: live.discard
    });
  }, [
    onStateChange,
    sessionId,
    ended,
    live.dirty,
    live.owner,
    live.scope,
    live.discard,
    saving,
    statusLabel
  ]);
  const tools = (
    <>
      <LiveSectionsToolbar
        key={live.scope}
        sections={sections}
        rows={live.rows}
        canEdit={live.canEdit && !freshResetBusy && !live.saving}
        onDraftExitChange={onDraftExitChange}
      />
      <LiveRecordings
        key={`vod:${live.scope}`}
        sessionId={sessionId}
        canEdit={live.canEdit && !freshResetBusy && !live.saving}
        refresh={refreshRecording}
      />
    </>
  );
  const exportText = () => {
    const text = visibleRows
      .map(row => {
        const body =
          !row.dirty && row.segment.revision === 0
            ? row.segment.machine_text
            : liveUtteranceText(row.segment.utterance);
        return `${row.segment.utterance.speaker || t('speaker')}:\n${body}`;
      })
      .join('\n\n');
    const url = URL.createObjectURL(
      new Blob([text], { type: 'text/plain;charset=utf-8' })
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = sections.selected
      ? `${sections.selected.name.replace(/[\\/:]/g, '-')}.txt`
      : `live-transcript-${sessionId}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };
  const Root = embedded ? 'div' : 'main';

  return (
    <Root
      className={
        embedded ? 'realtime-live-panel' : 'flex flex-col h-full p-4 gap-3'
      }
      data-live-dirty={live.dirty}
      data-live-count={live.rows.length}
      style={{
        backgroundColor: getColour('baseLayer'),
        color: getColour('normalText'),
        height: embedded ? '100%' : 'calc(100vh - 48px)'
      }}
    >
      <header className="flex flex-wrap gap-3 items-center">
        {!embedded && (
          <Button
            variant="outline"
            size="small"
            icon={<ArrowLeft size={16} />}
            label={t('back')}
            aria-label={t('back')}
            onClick={() => navigate('/')}
          />
        )}
        <div className={embedded ? 'hidden' : 'flex-1 min-w-40'}>
          <h1 className="font-semibold text-lg">{t('live-transcript')}</h1>
          <p
            className="text-sm"
            role="status"
            style={{ color: getColour('inactiveText') }}
          >
            {statusLabel}
            {' · '}
            {live.rows.length} {t('live-segments')}
          </p>
        </div>
        <Button
          size="small"
          icon={<Save size={16} />}
          label={embedded ? t(saving ? 'saving' : 'save') : saveLabel}
          aria-label={saveLabel}
          onClick={() => {
            void (sections.selected ? sections.update('save') : live.save());
          }}
          disabled={
            sectionFormOpen ||
            !canEdit ||
            (!sections.selected && !live.dirty) ||
            saving
          }
        />
        <Button
          variant="outline"
          size="small"
          icon={<Download size={16} />}
          label={t('export')}
          aria-label={t('export')}
          onClick={exportText}
          disabled={!visibleRows.length}
        />
        {(embedded || (!ended && live.owner)) && (
          <Button
            variant="outline"
            size="small"
            icon={<Square size={16} />}
            label={t(embedded ? 'stop' : 'live-stop-transcription')}
            aria-label={t('live-stop-transcription')}
            disabled={!live.owner?.can_stop || ended}
            onClick={() => {
              void live.stop();
            }}
          />
        )}
      </header>
      <LiveTranscriptSectionNotices
        live={live}
        sections={sections}
        embedded={embedded}
        visibleRows={visibleRows}
        canEdit={canEdit}
        draftExit={draftExit}
      />
      {embedded ? toolsSlot && createPortal(tools, toolsSlot) : tools}
      {!embedded && !live.loading && !live.rows.length && (
        <p className="p-8 text-center">{t('live-waiting-for-finals')}</p>
      )}
      <div
        ref={follow.viewport}
        onFocusCapture={follow.onFocusCapture}
        onBlurCapture={follow.onBlurCapture}
        className={embedded ? 'realtime-live-list' : 'flex-1 min-h-0'}
      >
        {embedded && !live.rows.length && (
          <p className="realtime-live-waiting">
            {t(live.loading ? 'loading' : 'live-waiting-for-finals')}
          </p>
        )}
        <Virtuoso
          ref={list}
          data={visibleRows}
          style={{ height: '100%' }}
          computeItemKey={(_, row) =>
            `${sections.renderScope}:${row.segment.segment_id}:${row.editorEpoch ?? 0}`
          }
          increaseViewportBy={300}
          atBottomThreshold={4}
          followOutput={follow.followOutput}
          atBottomStateChange={follow.atBottomStateChange}
          itemContent={(_, row) => (
            <div className="pb-3">
              <LiveEditorRow
                row={row}
                cache={cache}
                canEdit={canEdit}
                update={live.edit}
                updateSpeaker={live.editSpeaker}
              />
            </div>
          )}
        />
      </div>
      <footer
        className="flex justify-between items-center text-sm"
        style={{ color: getColour('inactiveText') }}
      >
        <span>
          {embedded && `${visibleRows.length} ${t('live-segments')} · `}
          {live.dirty ? t('live-unsaved') : t('live-saved')}
          {!canEdit && ` · ${t('read-only')}`}
        </span>
        <Button
          variant="outline"
          size="small"
          label={t('live-latest-text')}
          disabled={!visibleRows.length}
          onClick={() => {
            follow.resume();
            list.current?.scrollToIndex({
              index: visibleRows.length - 1,
              align: 'end'
            });
          }}
        />
      </footer>
    </Root>
  );
}
